import { TFnType } from '@tolgee/react';
import { isNil, initial } from 'lodash-es';
import { PerformanceName, Section } from 'services/api/dashboard';
import { platformLabelMap } from 'services/config/platform';
import toLowerCase from 'utils/toLowerCase';

type ValueType = Util.PickByType<string, 'string'>;

export type PerformanceLabelMapType = {
  readonly [K in PerformanceName]: [ValueType, ValueType, ValueType];
};
const sortedValue = ['platform', 'storeType', 'region', 'domain'] as const;

const PERFORMANCE_ECHARTS_LABEL_MAP: PerformanceLabelMapType = {
  // 订单概况页面
  REORDER_RATIO: ['下单总量', '复购单量', '复购订单占比'],
  // 销售转化页面
  PRE_SALE_CONSULT_CONVERSION_RATE: ['咨询买家数', '咨询后下单买家数', '咨询转化率'],
  OVERALL_CONSULT_CONVERSION_RATE: ['咨询买家数', '咨询后下单买家数', '咨询转化率'],
  ORDER_REMINDER_CONVERSION_RATE: ['催单买家数', '催单后下单买家数', '催单转化率'],
  PAYMENT_REMINDER_CONVERSION_RATE: ['催付订单量', '催付成功订单量', '催付转化率'],

  // 非COD订单损耗
  NOT_COD_OVERALL_LOSS_RATE: ['下单总量', '订单损耗量', '订单损耗率'],
  NOT_COD_ORDER_TO_PAYMENT_LOSS_RATE: ['下单总量', '订单损耗量', '订单损耗率'],
  NOT_COD_PAYMENT_TO_READY_TO_SHIP_LOSS_RATE: ['下单总量', '订单损耗量', '订单损耗率'],
  NOT_COD_PAYMENT_TO_PACKED_LOSS_RATE: ['下单总量', '订单损耗量', '订单损耗率'],
  NOT_COD_PACKED_TO_READY_TO_SHIP_LOSS_RATE: ['下单总量', '订单损耗量', '订单损耗率'],
  NOT_COD_READY_TO_SHIP_TO_SHIPPED_LOSS_RATE: ['下单总量', '订单损耗量', '订单损耗率'],
  NOT_COD_SHIPPED_TO_RECEIVED_LOSS_RATE: ['下单总量', '订单损耗量', '订单损耗率'],
  NOT_COD_RECEIVED_TO_CLOSED_LOSS_RATE: ['下单总量', '订单损耗量', '订单损耗率'],

  // COD订单损耗
  COD_OVERALL_LOSS_RATE: ['下单总量', '订单损耗量', '订单损耗率'],
  COD_ORDER_TO_CONFIRMED_LOSS_RATE: ['下单总量', '订单损耗量', '订单损耗率'],
  COD_CONFIRMED_TO_READY_TO_SHIP_LOSS_RATE: ['下单总量', '订单损耗量', '订单损耗率'],
  COD_CONFIRMED_TO_PACKED_LOSS_RATE: ['下单总量', '订单损耗量', '订单损耗率'],
  COD_PACKED_TO_READY_TO_SHIP_LOSS_RATE: ['下单总量', '订单损耗量', '订单损耗率'],
  COD_READY_TO_SHIP_TO_SHIPPED_LOSS_RATE: ['下单总量', '订单损耗量', '订单损耗率'],
  COD_SHIPPED_TO_RECEIVED_LOSS_RATE: ['下单总量', '订单损耗量', '订单损耗率'],
  COD_RECEIVED_TO_CLOSED_LOSS_RATE: ['下单总量', '订单损耗量', '订单损耗率'],

  // 评价数据页面
  BAD_COMMENT_RATE: ['评论数', '中差评数', '中差评率'],
  PRAISE_REMINDER_CONVERSION_RATE: ['自动催好评订单数', '自动催好评成功数', '自动催好评成功率'],
  FIX_COMMENT_REMINDER_CONVERSION_RATE: ['中差评数量', '成功挽回中差评数', '成功挽回中差评数比例'],
};

/**
 * 根据指标名称获取echarts的name
 * @param performanceName 指标名称
 * @returns
 */
// eslint-disable-next-line import/prefer-default-export
export function getEchartsNameByPerformanceName(performanceName?: PerformanceName) {
  if (isNil(performanceName)) {
    return ['损耗订单数', '挽回订单数', '订单挽回率'] as ValueType[];
  }
  return PERFORMANCE_ECHARTS_LABEL_MAP[performanceName];
}

/**
 * 翻译区域
 * @param section
 * @param t 翻译数据看板默认类型
 * @param trans 翻译区域
 * @returns platform-storeType-region-domain
 */
export const translateSection = (section: Section, t: TFnType) => {
  const sortedSection = isNil(section?.domain) ? initial(sortedValue) : sortedValue;
  return sortedSection
    .map((rs) => {
      switch (rs) {
        case 'storeType':
          if (section.storeType === '所有类型') {
            return t(`ViewDashboard.${section.storeType}`);
          }
          return t(`ViewDashboard.${section.storeType}店铺`);
        case 'domain':
          return t(`ViewDashboard.${section.domain}`);
        case 'platform':
          if (section.platform === '所有平台') {
            return t(`ViewDashboard.${section.platform}`);
          }
          return platformLabelMap[toLowerCase(section.platform)];
        case 'region':
          if (section.region === '所有地区') {
            return t(`ViewDashboard.${section.region}`);
          }
          return t(`region.${toLowerCase(section.region)}`);
        default:
          return undefined;
      }
    })
    .join('/');
};
